<template>
  <component :is="tourLayout" v-if="actor">
    <div
      class="actor-image w-full h-96 sm:h-80 relative bg-cover bg-center bg-no-repeat"
      :style="{
        '--thumb-url': `url('${heroImages.thumb}')`,
        '--hero-thumb': `url('${heroImages.hero}')`,
      }"
    >
      <div
        class="inset-0 absolute"
        style="
          background: linear-gradient(
            210deg,
            rgba(0, 0, 0, 0) 25%,
            rgba(0, 0, 0, 0.9) 100%
          );
        "
      />
      <div class="relative w-full max-w-7xl h-full mx-auto">
        <div class="absolute inset-3 justify-between items-end flex flex-row">
          <div class="w-full leading-snug">
            <h1 class="text-white text-xl font-bold">
              {{ actor.name }}
            </h1>
            <p class="text-white">
              Rank: {{ actor.rank }}
              <br />
              Movies: {{ actor.videoCount }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <AppBreadcrumb :trail="trail" />

    <nav
      v-if="alsoOn.length"
      aria-label="Also on"
      class="w-full max-w-7xl flex flex-wrap gap-x-3 gap-y-1 px-3 xl:px-0 text-sm"
    >
      <span class="font-bold">{{ actor.name }} is also on:</span>
      <ul class="contents">
        <li v-for="site in alsoOn" :key="`also-on-${site.url}`">
          <a :href="site.url" class="underline hover:no-underline">
            {{ site.name }}
          </a>
        </li>
      </ul>
    </nav>

    <div class="lg:hidden text-2xl uppercase font-black my-4 px-3 text-center">
      The Hottest Videos Featuring {{ actor.name }}
    </div>
    <AppDivider
      v-if="currentSite.slug === 'pornplus'"
      class="hidden lg:flex my-4"
    >
      <span class="text-3xl uppercase font-black whitespace-wrap">
        The Hottest Videos Featuring {{ actor.name }}
      </span>
    </AppDivider>

    <SectionContainer
      :title="
        currentSite.slug === 'pornplus'
          ? null
          : `${currentSite.name} ${actor.name} Movies`
      "
    >
      <div class="card-grid">
        <ReleaseCard
          v-for="release in releases.items"
          :key="release.id"
          :release="release"
          :vertical="useVerticalCards"
        />
      </div>
    </SectionContainer>

    <Pagination
      v-if="totalPages > 1"
      :resource="path.slice(1)"
      :current-page="page"
      :total-pages="totalPages"
    />
  </component>
</template>

<script setup>
  // Remount per page, so a page link fetches that page.
  definePageMeta({ key: (route) => route.fullPath })

  useRedirectInAgeRestrictedArea()

  const currentSite = inject("currentSite")
  const route = useRoute()
  const { notFoundUrl, useVerticalCards } = useSiteSettings(currentSite.value)
  const tourLayout = useTourLayout(currentSite.value)
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")

  const page = Math.max(1, parseInt(route.query.page) || 1)
  const perPage = 24
  const path = `/models/${encodeURIComponent(route.params.slug)}`

  const [{ data: actor }, { data: releases }] = await Promise.all([
    useApi(
      `/api/actors/${route.params.slug}?age_restricted=${inAgeRestrictedLocation}`,
    ),
    useApi(`/api/actors/${route.params.slug}/releases`, {
      query: {
        page,
        per_page: perPage,
        age_restricted: inAgeRestrictedLocation,
      },
    }),
  ])

  const totalPages = computed(() => releases.value?.pagination?.totalPages || 1)

  // Her model pages on the sister sites in this network (same performer, same
  // slug). Plain followed links, rendered on the server.
  const requestUrl = useRequestURL().href
  const alsoOn = computed(() =>
    (actor.value?.alsoOn ?? [])
      .map((site) => ({
        name: site.name,
        url: sisterModelUrl(requestUrl, site),
      }))
      .filter((site) => site.name && site.url),
  )

  const trail = computed(() => [
    { name: currentSite.value.name, path: "/" },
    { name: "Models", path: "/models" },
    { name: actor.value?.name },
  ])

  // A missing, path-less (CDN 403) or broken image gets the locked "join now"
  // placeholder.
  // These are CSS backgrounds, which fire no error event, so probe them once
  // on the client.
  const failedImages = ref([])
  const heroImage = (url, placeholder) =>
    isUsableImageUrl(url) && !failedImages.value.includes(url)
      ? url
      : placeholder
  const heroImages = computed(() => ({
    thumb: heroImage(actor.value?.thumbUrl, "/images/model-placeholder.webp"),
    hero: heroImage(
      actor.value?.heroThumb,
      "/images/model-placeholder-hero.webp",
    ),
  }))
  onMounted(() => {
    for (const url of [actor.value?.thumbUrl, actor.value?.heroThumb]) {
      if (!isUsableImageUrl(url)) continue
      const probe = new Image()
      probe.onerror = () => failedImages.value.push(url)
      probe.src = url
    }
  })

  if (!actor.value) {
    navigateTo(notFoundUrl, { external: true, statusCode: 302 })
  } else if (page > totalPages.value) {
    await navigateTo(path, { redirectCode: 302 })
  } else {
    // Scenes on this site only; 0 drops the count from the title. The API
    // formats the count with delimiters ("1,234"), so strip them first.
    const sceneCount =
      parseInt(String(actor.value.videoCount ?? "").replace(/\D/g, "")) || 0

    // Sites with their own niche and quality words get copy built from them;
    // the rest keep the generic templates.
    const copy = siteCopyFor(currentSite.value)
    const seoSite = currentSite.value.alias ?? currentSite.value
    const performer = {
      siteName: seoSite.seoSiteName,
      name: actor.value.name,
      sceneCount,
      gender: actor.value.gender,
    }

    useSiteSeo(
      {
        actorName: actor.value.name,
        actorNames: actor.value.name,
        defaultDescription: copy
          ? "{{{ modelDescription }}}"
          : "{{#sceneCount}}Watch {{{ actorName }}} in {{ scenesLabel }} on {{{ siteName }}}.{{/sceneCount}}{{^sceneCount}}Watch {{{ actorName }}} on {{{ siteName }}}.{{/sceneCount}} Full-length videos, updated weekly.",
        defaultTitle: copy
          ? "{{{ modelTitle }}}"
          : "{{{ actorName }}}{{#sceneCount}} – {{ scenesLabel }}{{/sceneCount}} | {{{ siteName }}}",
        modelTitle: copy && modelTitle(performer),
        modelDescription: copy && modelDescription({ ...performer, copy }),
        pageNumber: page,
        sceneCount,
        scenesLabel: `${sceneCount} ${sceneCount === 1 ? "scene" : "scenes"}`,
      },
      {},
      // Performer titles follow the SEO guide in code, not per-site templates.
      { siteTemplates: false, canonicalPage: page },
    )

    const origin = publicSiteOrigin(useRequestURL().href)
    const schema = buildProfilePageSchema({
      origin,
      url: `${origin}${page > 1 ? `${path}?page=${page}` : path}`,
      slug: actor.value.cachedSlug,
      name: actor.value.name,
      image: actor.value.thumbUrl,
      trail: trail.value,
    })

    if (schema) {
      useHead({
        script: [
          {
            key: "schema-ProfilePage",
            type: "application/ld+json",
            innerHTML: serializeJsonLd(schema),
          },
        ],
      })
    }
  }
</script>

<style lang="scss" scoped>
  .actor-image {
    background-image: var(--thumb-url);

    @media (min-width: 768px) {
      background-image: var(--hero-thumb);
    }
  }
</style>
